<script setup>
import {computed, onMounted, ref} from "vue";
import Vue from "vue";
import {usePiniaStore} from "../../store/pinia_store";
import {isEmpty} from "lodash";

const props = defineProps({
  disabledDeliveryDates: {
    type: Array,
    default: null
  },
  dentaidApiInstance: {
    type: Function,
    required: true
  },
  commentLength: {
    type: Number,
    default: 100
  },
});

const piniaStore = usePiniaStore();

const deliveryDateLabel = window.Drupal.t('Your delivery date');
const deliveryDateInfo = window.Drupal.t('Your order will be fulfilled by our wholesaler. Delivery time may vary depending on the wholesaler.');

const checkoutTitleTxt = window.Drupal.t('Check and complete order');
const commentLabel = window.Drupal.t('Space for a comment that we can take into account');
const referenceLabel = window.Drupal.t('Personal order reference');
const commentPlaceholder = window.Drupal.t('Comment');
const referencePlaceholder = window.Drupal.t('Reference');
const wholesalerLabel = window.Drupal.t('Wholesaler');
const wholesalerPlaceholder = window.Drupal.t('Choose a wholesaler')
const noProductsText = window.Drupal.t('No products in cart');
const commentMaxLengthLabel = window.Drupal.t('Maximum length of 100 characters allowed');
const referenceMaxLengthLabel = window.Drupal.t('Maximum length of 30 characters allowed');
const deliveryDate = ref(null);
const hasCartLines = ref(true);
const comment = ref('');
const reference = ref('');
const eventBus = new Vue();

const shipFromMainLocation = computed(() => {
  return piniaStore.cart?.conditions?.ship_from_main_location;
})

const wholesaler = ref('');

const canOrder = computed(() => {
  return piniaStore.cart?.conditions?.can_checkout ?? false;
});

const handleInput = (value) => {
  deliveryDate.value = value;
}

const onKeyDown = (event) => {
  if (comment.value.length >= props.commentLength) {
    if (event.keyCode >= 48 && event.keyCode <= 90) {
      event.preventDefault();
    }
  }
}

onMounted(() => {
  eventBus.$on('get-cart-completed', (payload) => {
    hasCartLines.value = payload;
  });
})
</script>

<template>
  <div class="vue-checkout-confirm-wrapper">
    <div class="container">
      <div class="row">
        <div class="col-12">
          <h1>{{ checkoutTitleTxt }}</h1>
        </div>
      </div>

      <div class="row" v-if="hasCartLines">
        <div class="col-lg-8 extra-information">
          <dentaid-delivery-datepicker
            v-show="!piniaStore.customer?.is_pharma ?? false"
            :disabled-delivery-dates="disabledDeliveryDates"
            @input="handleInput"
          ></dentaid-delivery-datepicker>

          <div class="mb-7" v-show="piniaStore.customer?.is_pharma ?? false">
            <p class="font-weight-bold input-label mb-1">{{ deliveryDateLabel }}</p>
            <p class="delivery-info">{{ deliveryDateInfo }}</p>
          </div>

          <div class="mb-7" v-if="piniaStore.customer?.is_pharma ?? false">
            <p class="small">{{ wholesalerLabel }}</p>
            <select v-if="shipFromMainLocation" v-model="wholesaler">
              <option value="">Dentaid Houten</option>
            </select>

            <select v-else v-model="wholesaler" required>
              <option value="">{{ wholesalerPlaceholder }}</option>
              <option :value="wholesaler.id" v-for="wholesaler in piniaStore.customer?.customer_group?.wholesalers">
                {{ wholesaler.name }}
              </option>
            </select>
          </div>

          <div class="order-reference mb-7">
            <p class="small">{{ referenceLabel }}</p>
            <input type="text" v-model="reference" :placeholder="referencePlaceholder" maxlength="30"/>
            <small>{{ referenceMaxLengthLabel }}</small>
          </div>

          <div class="order-comment mb-7">
            <p class="small">{{ commentLabel }}</p>
            <textarea v-model="comment" rows="2" :placeholder="commentPlaceholder" @keydown="onKeyDown"
                      maxlength="100"></textarea>
            <small>{{ commentMaxLengthLabel }}</small>
          </div>

        </div>

        <div class="col-xl-3 offset-xl-1 col-lg-4 order-overview">
          <dentaid-mini-cart
            :dentaid-api-instance="dentaidApiInstance"
            :event-bus="eventBus"
            :jwt-token="piniaStore.parsedToken"
            :delivery-date="deliveryDate"
            :comment="comment"
            :reference="reference"
            :wholesaler="wholesaler"
            :can-order="canOrder"
          ></dentaid-mini-cart>
        </div>
      </div>

      <div class="row" v-if="!hasCartLines">
        <div class="col-12">
          <h2>{{ noProductsText }}</h2>
        </div>
      </div>
    </div>
  </div>
</template>
